import { usePostHog } from 'posthog-js/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect } from 'react';
import { useUserData } from './useUserData';

interface PostHogUser {
  id: string;
  email?: string;
  name?: string;
  plan?: string;
  credits_remaining?: number;
}

export const usePostHogTracking = () => {
  const posthog = usePostHog();
  const { user, isAuthenticated } = useAuth0();
  const { userData } = useUserData();

  // Identify user when they log in
  const identifyUser = useCallback(() => {
    if (!posthog || !isAuthenticated || !user?.sub) return;

    const userProperties: PostHogUser = {
      id: user.sub,
      email: user.email,
      name: user.name,
    };

    // Add user data from our database if available
    if (userData) {
      userProperties.plan = userData.plan || 'free';
      userProperties.credits_remaining = userData.credits_remaining;
    }

    posthog.identify(user.sub, userProperties);
  }, [posthog, isAuthenticated, user, userData]);

  // Auto-identify user when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      identifyUser();
    }
  }, [isAuthenticated, user?.sub, userData, identifyUser]);

  // Track CV upload submission
  const trackCVUploadSubmitted = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('cv_upload_submitted', {
      service_type: 'cv_upload',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      ...properties
    });
  }, [posthog, isAuthenticated]);

  // Track CV tailoring request
  const trackTailorCVRequested = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('tailor_cv_requested', {
      service_type: 'cv_tailoring',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      user_plan: userData?.plan || 'free',
      credits_remaining: userData?.credits_remaining,
      ...properties
    });
  }, [posthog, isAuthenticated, userData]);

  // Track CV download request
  const trackDownloadCVRequested = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('download_cv_requested', {
      service_type: 'cv_download',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      download_format: 'docx',
      ...properties
    });
  }, [posthog, isAuthenticated]);

  // Track cover letter generation request
  const trackCoverLetterRequested = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('cover_letter_requested', {
      service_type: 'cover_letter_generation',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      user_plan: userData?.plan || 'free',
      credits_remaining: userData?.credits_remaining,
      ...properties
    });
  }, [posthog, isAuthenticated, userData]);

  // Track cover letter download request
  const trackDownloadCoverLetterRequested = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('download_cover_letter_requested', {
      service_type: 'cover_letter_download',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      download_format: 'docx',
      ...properties
    });
  }, [posthog, isAuthenticated]);

  // Track ATS score request
  const trackATSScoreRequested = useCallback((properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture('ats_score_requested', {
      service_type: 'ats_analysis',
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      ...properties
    });
  }, [posthog, isAuthenticated]);

  // Generic event tracking function
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    if (!posthog) return;
    
    posthog.capture(eventName, {
      timestamp: new Date().toISOString(),
      user_authenticated: isAuthenticated,
      ...properties
    });
  }, [posthog, isAuthenticated]);

  return {
    identifyUser,
    trackCVUploadSubmitted,
    trackTailorCVRequested,
    trackDownloadCVRequested,
    trackCoverLetterRequested,
    trackDownloadCoverLetterRequested,
    trackATSScoreRequested,
    trackEvent,
    posthog
  };
};
